/**
 * Circling-area geometry — stadium polygon + sector wedge builders.
 *
 * Builds the GeoJSON polygon that visualizes the legal circle-to-land area
 * around a runway for the active aircraft category. The "stadium" shape is
 * the FAA TERPS circling maneuvering-area definition: a rectangle along the
 * runway centerline with a semicircular cap at each threshold, the cap
 * radius equal to the AIM 5-4-29 table radius for the active CAT + MDA-MSL
 * band. Sector wedges represent the "Circling NA <direction> of RWY X"
 * exclusions parsed from the plate notes.
 *
 * Safety contract:
 *   - This is THE safety-most-sensitive geometry surface in Fyrefly. A
 *     misrendered polygon misleads a pilot into TERPS-prohibited airspace.
 *   - All math goes through src/lib/geo.ts (destinationPoint), which is
 *     itself Codex-cross-checked. Don't re-derive WGS-84 destinationPoint
 *     math here.
 *   - GeoJSON convention: rings are [lon, lat] (NOT [lat, lon]); first ===
 *     last to close. polygon-clipping uses the same convention.
 *   - Polygon subtraction is delegated to polygon-clipping's difference()
 *     — battle-tested. Roll-your-own would be a safety regression.
 *
 * Cross-checked against an independent OpenAI Codex implementation
 * 2026-05-17 — Codex verified the stadium formula and bearing-wraparound
 * handling. See data/circling-minima-phase-c-verification.md.
 */

import polygonClipping from 'polygon-clipping';
import { destinationPoint, bearingDeg, greatCircleNm } from './geo';

// ─── Stadium polygon ─────────────────────────────────────────────────────────

/**
 * Build the closed "stadium" polygon (rectangle + two semicircular caps)
 * around a runway centerline.
 *
 * The polygon is the set of points within `radiusNm` of the segment from
 * `rwy1` to `rwy2` — equivalently, the Minkowski sum of the segment with a
 * disc of radius `radiusNm`. Rendered as a closed GeoJSON Polygon ring.
 *
 * @param rwy1     First runway threshold (lat/lon).
 * @param rwy2     Other runway threshold (lat/lon).
 * @param radiusNm Cap radius (NM) — from AIM 5-4-29 for the active CAT.
 * @param segments Polygon resolution. Default 64 — same density as
 *                 geodesicCircleRing. Half-cap uses segments/2 vertices.
 */
export function buildCirclingStadium(
  rwy1: { lat: number; lon: number },
  rwy2: { lat: number; lon: number },
  radiusNm: number,
  segments = 64,
): GeoJSON.Polygon {
  // Degenerate: same threshold both ends → return a geodesic circle.
  // Without this, bearingDeg returns NaN and the stadium math NaN-poisons.
  if (rwy1.lat === rwy2.lat && rwy1.lon === rwy2.lon) {
    return {
      type: 'Polygon',
      coordinates: [buildGeodesicRing(rwy1.lat, rwy1.lon, radiusNm, segments)],
    };
  }

  // Bearing along the centerline (θ = rwy1 → rwy2). The two parallel
  // sidewalls run at bearings θ+90 and θ-90 offset from the centerline
  // by `radiusNm`. Construction order:
  //   1. rwy1 back-cap: half-circle from θ+90 clockwise through θ-180
  //      to θ-90 (the half facing away from rwy2)
  //   2. sidewall: straight line from rwy1's θ-90 offset to rwy2's θ-90
  //      offset
  //   3. rwy2 forward-cap: half-circle from θ-90 clockwise through θ
  //      to θ+90 (the half facing away from rwy1)
  //   4. sidewall: closing line back to rwy1's θ+90 offset (the first
  //      vertex emitted), closing the ring.
  const theta = bearingDeg(rwy1.lat, rwy1.lon, rwy2.lat, rwy2.lon);
  const halfCapSegments = Math.max(2, Math.floor(segments / 2));

  const ring: Array<[number, number]> = [];

  // Cap 1 — half-circle around rwy1, swept from θ+90 clockwise through
  // θ-180 to θ-90 (i.e. the half facing away from rwy2). 180° sweep.
  for (let i = 0; i <= halfCapSegments; i++) {
    const frac = i / halfCapSegments; // 0..1
    const brg = normalizeBearing(theta + 90 + frac * 180);
    const p = destinationPoint(rwy1.lat, rwy1.lon, brg, radiusNm);
    ring.push([p.lon, p.lat]);
  }
  // ring now ends at rwy1's θ-90 offset (right side, near rwy1).

  // Right sidewall: straight line from rwy1's θ-90 offset to rwy2's
  // θ-90 offset. The cap loop already deposited the start point; just
  // walk to the end.
  // (No interior densification — the line is short enough that
  // Mercator-vs-geodesic divergence is sub-pixel at typical zooms.)
  const r2Right = destinationPoint(rwy2.lat, rwy2.lon, normalizeBearing(theta - 90), radiusNm);
  ring.push([r2Right.lon, r2Right.lat]);

  // Cap 2 — half-circle around rwy2, swept from θ-90 clockwise through
  // θ to θ+90 (i.e. the half facing away from rwy1). 180° sweep.
  for (let i = 0; i <= halfCapSegments; i++) {
    const frac = i / halfCapSegments;
    const brg = normalizeBearing(theta - 90 + frac * 180);
    const p = destinationPoint(rwy2.lat, rwy2.lon, brg, radiusNm);
    ring.push([p.lon, p.lat]);
  }
  // ring now ends at rwy2's θ+90 offset (left side, near rwy2).

  // Left sidewall: straight line back to rwy1's θ+90 offset. The next
  // closing point is the very first vertex (rwy1's θ+90 offset).
  ring.push([ring[0][0], ring[0][1]]);

  return {
    type: 'Polygon',
    coordinates: [ring],
  };
}

// ─── Sector wedge (for exclusions) ───────────────────────────────────────────

/**
 * Build a sector-wedge polygon — a pie slice from a pivot point, sweeping
 * clockwise from `startBrgDeg` to `endBrgDeg`, extending `extentNm` from
 * the pivot. Used as the "subtract this from the stadium" geometry for
 * "Circling NA <direction>" restrictions.
 *
 * The sweep ALWAYS goes clockwise from start to end. If `endBrgDeg <
 * startBrgDeg` (e.g. 350° → 10°), the walker wraps through 360° so the
 * resulting wedge spans the SHORT clockwise arc, not the long way around.
 * A 360° wedge (start === end after normalization) renders as a full
 * disc.
 *
 * @param pivot       Wedge apex (lat/lon) — for V1 always the airport ARP.
 * @param startBrgDeg Start bearing (geographic compass, N=0, E=90, ...).
 * @param endBrgDeg   End bearing, clockwise from start.
 * @param extentNm    Wedge radius. Use 2× stadium max radius so the
 *                    subtraction fully clips through the stadium.
 * @param segments    Arc resolution. Default 32 — matches geo.ts conventions.
 */
export function buildSectorWedge(
  pivot: { lat: number; lon: number },
  startBrgDeg: number,
  endBrgDeg: number,
  extentNm: number,
  segments = 32,
): GeoJSON.Polygon {
  // Normalize start/end into [0, 360). Compute the clockwise sweep amount.
  const start = normalizeBearing(startBrgDeg);
  const end = normalizeBearing(endBrgDeg);
  let sweep = end - start;
  if (sweep <= 0) sweep += 360;
  // When start === end exactly, normalize() sends both to the same value
  // and sweep becomes 360 (full disc). That's the right behavior — a
  // user explicitly specifying "circling NA all directions" via sectors
  // should produce a full ring.

  const ring: Array<[number, number]> = [];
  // Start at the pivot.
  ring.push([pivot.lon, pivot.lat]);
  // Walk the arc clockwise from start to start+sweep, inclusive of both
  // ends. We use `segments` arc segments → `segments+1` arc vertices.
  for (let i = 0; i <= segments; i++) {
    const frac = i / segments;
    const brg = normalizeBearing(start + frac * sweep);
    const p = destinationPoint(pivot.lat, pivot.lon, brg, extentNm);
    ring.push([p.lon, p.lat]);
  }
  // Close back to the pivot.
  ring.push([pivot.lon, pivot.lat]);

  return {
    type: 'Polygon',
    coordinates: [ring],
  };
}

// ─── Polygon subtraction ────────────────────────────────────────────────────

/**
 * Subtract one or more sector wedges from the stadium polygon.
 *
 * Result is the legal-to-circle area. If every wedge fully covers the
 * stadium, returns an empty array (caller renders no polygon, just the
 * "circling NA" legend chip). If a wedge is entirely outside the stadium,
 * the stadium is returned unchanged. If a wedge is entirely inside, the
 * stadium gets a hole (rendered as a donut).
 *
 * @returns MultiPolygon coordinates: array of polygons, each an array of
 *   rings, each ring an array of [lon, lat] points. Empty array =
 *   completely subtracted.
 */
export function subtractWedgesFromStadium(
  stadium: GeoJSON.Polygon,
  wedges: GeoJSON.Polygon[],
): number[][][][] {
  if (wedges.length === 0) {
    // Wrap the single polygon as a MultiPolygon-shaped result.
    return [stadium.coordinates as number[][][]];
  }
  // polygon-clipping uses MultiPolygon-shaped tuples. Cast through
  // unknown so the typed shapes (Polygon coordinates carry `Position[]`
  // which is `number[]`, not `[number, number]`) line up with
  // polygon-clipping's stricter `[x, y]` Pair type. The runtime shape
  // is identical.
  type Pair = [number, number];
  type Ring = Pair[];
  type Poly = Ring[];
  type Multi = Poly[];
  const stadiumPoly: Multi = [stadium.coordinates as unknown as Poly];
  const wedgePolys: Multi[] = wedges.map(
    (w) => [w.coordinates as unknown as Poly] as Multi,
  );
  // difference(subject, ...clippers) — clippers are spread, not array.
  const result = polygonClipping.difference(
    stadiumPoly as unknown as Parameters<typeof polygonClipping.difference>[0],
    ...(wedgePolys as unknown as Parameters<typeof polygonClipping.difference>[1][]),
  );
  return result as unknown as number[][][][];
}

/**
 * Convenience: turn a polygon-clipping MultiPolygon-shape into a
 * GeoJSON FeatureCollection of Polygon features. Empty input → empty
 * FeatureCollection (caller suppresses the source).
 */
export function multiPolygonToFeatureCollection(
  multi: number[][][][],
  properties: GeoJSON.GeoJsonProperties = {},
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: multi.map((rings) => ({
      type: 'Feature',
      properties,
      geometry: { type: 'Polygon', coordinates: rings },
    })),
  };
}

/** Convert one or more wedge polygons to a FeatureCollection (for the
 *  "excluded sectors" rendering — drawn separately from the stadium so
 *  hatch fill + magenta stroke style independently). */
export function wedgesToFeatureCollection(
  wedges: GeoJSON.Polygon[],
  properties: GeoJSON.GeoJsonProperties = {},
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: wedges.map((w) => ({
      type: 'Feature',
      properties,
      geometry: w,
    })),
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Normalize a compass bearing to [0, 360). Handles negatives + wraparound. */
function normalizeBearing(deg: number): number {
  let d = deg % 360;
  if (d < 0) d += 360;
  return d;
}

/** Build a closed geodesic-circle ring as [lon, lat] pairs. Inlined here
 *  rather than importing geo.geodesicCircleRing so the degenerate
 *  stadium case (rwy1 === rwy2) gets a polygon-clipping-safe ring (the
 *  geo helper closes the ring but doesn't promise the first/last pair
 *  are bit-exact — we want strict equality). */
function buildGeodesicRing(
  lat: number,
  lon: number,
  radiusNm: number,
  segments: number,
): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i < segments; i++) {
    const brg = (360 * i) / segments;
    const p = destinationPoint(lat, lon, brg, radiusNm);
    out.push([p.lon, p.lat]);
  }
  out.push([out[0][0], out[0][1]]); // strict close
  return out;
}

/**
 * Approximate the planar area of a ring (in NM²) using the Shoelace
 * formula on the equirectangular projection at the ring's mean latitude.
 * Accurate enough for unit-test sanity checks at sub-100-NM scales —
 * NOT for production "is this point inside the legal area" math (use a
 * proper geodesic point-in-polygon for that).
 *
 * Used by circlingGeometry.test.ts to verify the stadium area roughly
 * matches the theoretical `2·r·L + π·r²` formula.
 */
export function approximatePolygonAreaNm2(ring: ReadonlyArray<ReadonlyArray<number>>): number {
  if (ring.length < 3) return 0;
  // Mean latitude → meridian convergence factor (1° lon = 60 NM × cos(φ)).
  let sumLat = 0;
  for (const [, lat] of ring) sumLat += lat;
  const meanLat = sumLat / ring.length;
  const cosLat = Math.cos((meanLat * Math.PI) / 180);

  // Shoelace formula on (x, y) = (lon * cosLat * 60, lat * 60), units NM.
  let acc = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [lon1, lat1] = ring[i];
    const [lon2, lat2] = ring[i + 1];
    const x1 = lon1 * cosLat * 60;
    const y1 = lat1 * 60;
    const x2 = lon2 * cosLat * 60;
    const y2 = lat2 * 60;
    acc += x1 * y2 - x2 * y1;
  }
  return Math.abs(acc) / 2;
}

/** Convenience: compute the great-circle distance between two thresholds.
 *  Exposed so callers (the layer) can sanity-check that the chosen pair
 *  is actually two ends of one runway, not two random fixes. */
export function thresholdDistanceNm(
  rwy1: { lat: number; lon: number },
  rwy2: { lat: number; lon: number },
): number {
  return greatCircleNm(rwy1.lat, rwy1.lon, rwy2.lat, rwy2.lon);
}
